/* bb-cart-add.js — shared bundle-aware add-to-cart.
   Exposes window.BBCart.addBundle({ variantId, bundleId, giftIds, qty }).

   This is a faithful copy of the add sequence proven on the PDP CRO panel
   (assets/cro-panel.js → CROPanel.prototype.addToCart). It is deliberately
   duplicated rather than shared so the live PDP path stays frozen while the
   homepage quick-add reuses the exact same, verified cart mechanics:

     • gifts are cart-level singletons (de-duped against the current cart),
     • a bundle already in the cart is BUMPED via a verified /cart/update.js
       total (Simple Bundles kit lines never merge and keyed change.js
       misapplies deltas — see cro-panel.js for the full rationale),
     • first add sends the parent in its OWN /cart/add.js call, gifts follow,
     • parent stores its gift list in _bb_gifts so the drawer can clean up.

   On success it dispatches `cart:added` + `bb:cart:open` (unless opts.silent) so the shared cart
   drawer slides out — identical to the PDP. Returns a Promise so callers can
   drive their own loading / error UI. */

(function () {
  'use strict';

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      // /cart/add.js is all-or-nothing. On 422 the body carries the real
      // reason (`description`, `errors`, `status`) — surface it, don't treat
      // an OOS/422 as success.
      return r.json().then(function (body) {
        if (!r.ok || body.status) {
          var err = new Error(body.description || body.message || 'cart');
          err.shopify = body;
          throw err;
        }
        return body;
      });
    });
  }

  function getLineProp(item, name) {
    var props = item.properties || {};
    if (Array.isArray(props)) {
      var match = props.find(function (p) { return p[0] === name; });
      return match ? match[1] : null;
    }
    return props[name] || null;
  }

  function cartVariantTotal(cart, variantId) {
    return ((cart && cart.items) || []).reduce(function (n, it) {
      return n + (it.variant_id === variantId ? it.quantity : 0);
    }, 0);
  }

  // Set a variant's TOTAL cart quantity via /cart/update.js, verified against
  // a fresh read after the cart settles. The ONLY reliable mutation for Simple
  // Bundles kit lines. Throws if the total never lands so the caller can
  // surface a real error.
  function setVariantTotalVerified(variantId, target, attempts, ask) {
    attempts = attempts === undefined ? 3 : attempts;
    if (ask === undefined) ask = target;
    var updates = {};
    updates[variantId] = ask;
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        return new Promise(function (res) { setTimeout(res, 600); });
      })
      .then(function () {
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        var total = cartVariantTotal(cart, variantId);
        if (total === target) return cart;
        if (attempts <= 1) throw new Error('Could not update cart quantity. Please try again.');
        var nextAsk = total > target ? Math.max(0, ask - (total - target)) : target;
        return setVariantTotalVerified(variantId, target, attempts - 1, nextAsk);
      });
  }

  function addBundle(opts) {
    opts = opts || {};
    var variantId = parseInt(opts.variantId, 10);
    var bundleId = opts.bundleId || '';
    var qty = (opts.qty && opts.qty > 0) ? opts.qty : 1;
    var giftIds = (opts.giftIds || []).filter(function (id) {
      return typeof id === 'number' && id > 0;
    });
    if (!variantId) return Promise.reject(new Error('no variant'));

    // De-dupe gifts against current cart (gifts are cart-level singletons) and
    // decide first-add vs bump.
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var existingGiftVariants = {};
        (cart.items || []).forEach(function (it) {
          var props = it.properties || {};
          var hasGiftProp = Array.isArray(props)
            ? props.some(function (p) { return p[0] === '_bb_gift_for'; })
            : Object.prototype.hasOwnProperty.call(props, '_bb_gift_for');
          if (hasGiftProp) existingGiftVariants[it.variant_id] = true;
        });

        var giftItems = giftIds
          .filter(function (gid) { return !existingGiftVariants[gid]; })
          .map(function (gid) {
            return { id: gid, quantity: 1, properties: { '_bb_gift_for': bundleId } };
          });

        // Bundle already in cart → bump the variant's verified total instead
        // of re-adding (re-adds never merge and pile up hidden qty-1 lines).
        var existingParentQty = (cart.items || []).reduce(function (n, it) {
          return n + (it.variant_id === variantId &&
                      getLineProp(it, '_bb_bundle_id') === bundleId ? it.quantity : 0);
        }, 0);
        if (existingParentQty > 0) {
          return setVariantTotalVerified(variantId, cartVariantTotal(cart, variantId) + qty)
            .then(function () { return giftItems.length ? addItems(giftItems) : null; });
        }

        // First add of this bundle. Parent goes in its OWN add.js call
        // (batching with gifts changes the parent's line-key hash); gifts
        // follow. Parent stores its gift list in _bb_gifts for drawer cleanup.
        //
        // opts.properties lets a caller attach its own line-item properties to
        // the parent (the quiz uses it for quiz_id / quiz_source / quiz_rec /
        // quiz_visitor, which must survive onto the order line). Optional and
        // merged underneath the _bb_* keys, so existing callers are unaffected
        // and no caller can overwrite the bundle bookkeeping.
        var parentProps = {};
        if (opts.properties && typeof opts.properties === 'object') {
          for (var pk in opts.properties) {
            if (Object.prototype.hasOwnProperty.call(opts.properties, pk)) {
              parentProps[pk] = opts.properties[pk];
            }
          }
        }
        parentProps['_bb_bundle_id'] = bundleId;
        parentProps['_bb_gifts'] = giftIds.join(',');
        parentProps['_bundle_id'] = bundleId;

        return addItems([{
          id: variantId,
          quantity: qty,
          properties: parentProps
        }]).then(function () {
          return giftItems.length ? addItems(giftItems) : null;
        });
      })
      .then(function () {
        // opts.silent: the caller is navigating straight to checkout (landing
        // pages), so don't open the drawer; it flashed white on the way out.
        if (opts.silent) return;
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { silent: false } }));
        document.dispatchEvent(new CustomEvent('bb:cart:open'));
      });
  }

  window.BBCart = window.BBCart || {};
  window.BBCart.addBundle = addBundle;
})();
